import type WebampConstructor from "webamp";
import type { LoadedURLTrack, WindowLayout } from "webamp";
import {
	RADIO_EQUALIZER_SLIDERS,
	type RadioPreferences,
	restoreRadioPreferences,
} from "../app/desktop-preferences";
import {
	AudibleTrackClock,
	createAudibleClockMiddleware,
} from "./audible-track-clock";
import type { LivePresentation } from "./metadata-sync";
import { type RadioStation, stationTracks } from "./stations";

export type RadioPlaybackState =
	| "loading"
	| "playing"
	| "paused"
	| "stopped"
	| "blocked"
	| "closed"
	| "error";

export interface RadioSnapshot {
	closed: boolean;
	error: string | null;
	metadataFresh: boolean;
	playback: RadioPlaybackState;
	ready: boolean;
	station: RadioStation;
	title: string;
}

type Listener = (snapshot: RadioSnapshot) => void;
type PreferenceListener = (preferences: RadioPreferences) => void;
type WebampInstance = InstanceType<typeof WebampConstructor>;
type WebampState = ReturnType<WebampInstance["store"]["getState"]>;

type PrivateMedia = {
	_context?: AudioContext;
	_source?: { _audio?: HTMLMediaElement };
};

export type StationLoadAction = {
	id: number;
	type: "BUFFER_TRACK" | "PLAY_TRACK";
};

type ObservedMediaStatus = "PLAYING" | "STOPPED" | "PAUSED";

export interface MediaPlaybackObservation {
	contextRunning: boolean;
	ended: boolean;
	paused: boolean;
	pendingRoute: string | null;
	readyState: number;
	sourceRoute: string | null;
}

const PLAYBACK_START_TIMEOUT_MS = 8_000;

export function radioPreferencesFromWebampState(
	state: Pick<WebampState, "equalizer" | "media">,
): RadioPreferences {
	return restoreRadioPreferences({
		volume: state.media.volume,
		equalizer: {
			enabled: state.equalizer.on,
			auto: state.equalizer.auto,
			sliders: state.equalizer.sliders,
		},
	});
}

export function radioPreferencesEqual(
	left: RadioPreferences,
	right: RadioPreferences,
): boolean {
	if (
		left.volume !== right.volume ||
		left.equalizer.enabled !== right.equalizer.enabled ||
		left.equalizer.auto !== right.equalizer.auto
	) {
		return false;
	}
	return RADIO_EQUALIZER_SLIDERS.every(
		(slider) =>
			left.equalizer.sliders[slider] === right.equalizer.sliders[slider],
	);
}

export function playbackStateFromObservation(
	current: RadioPlaybackState,
	audible: boolean,
	mediaStatus: ObservedMediaStatus,
): RadioPlaybackState {
	if (audible) return "playing";
	if (current === "loading" || current === "blocked") return current;
	return mediaStatus === "PAUSED" ? "paused" : "stopped";
}

export function mediaIsReadyForStation({
	contextRunning,
	ended,
	paused,
	pendingRoute,
	readyState,
	sourceRoute,
}: MediaPlaybackObservation): boolean {
	if (paused || ended || !contextRunning || readyState < 2) return false;
	return pendingRoute === null || sourceRoute === pendingRoute;
}

export function mediaPlaybackAttemptIsActive({
	contextRunning,
	ended,
	paused,
}: Pick<
	MediaPlaybackObservation,
	"contextRunning" | "ended" | "paused"
>): boolean {
	return contextRunning && !paused && !ended;
}

export function playbackMessage(snapshot: RadioSnapshot): string {
	switch (snapshot.playback) {
		case "loading":
			return `CONNECTING... ${snapshot.station.channelLabel}`;
		case "blocked":
			return `PRESS PLAY... ${snapshot.station.channelLabel}`;
		case "paused":
			return `PAUSED... ${snapshot.title}`;
		case "stopped":
			return `STOPPED... ${snapshot.title}`;
		case "error":
			return "STREAM UNAVAILABLE";
		default:
			return snapshot.title;
	}
}

export function stationLoadAction(
	trackOrder: readonly number[],
	stationIndex: number,
	play: boolean,
): StationLoadAction | null {
	const trackId = trackOrder[stationIndex];
	if (trackId == null) return null;
	return { type: play ? "PLAY_TRACK" : "BUFFER_TRACK", id: trackId };
}

const DESKTOP_LAYOUT: WindowLayout = {
	main: { position: { top: 0, left: 0 }, closed: false },
	equalizer: { position: { top: 116, left: 0 }, closed: false },
	playlist: {
		position: { top: 232, left: 0 },
		size: { extraHeight: 3, extraWidth: 0 },
		closed: false,
	},
};

const COMPACT_LAYOUT: WindowLayout = {
	main: { position: { top: 0, left: 0 }, closed: false },
	equalizer: { position: { top: 116, left: 0 }, closed: false },
	playlist: {
		position: { top: 232, left: 0 },
		size: { extraHeight: 1, extraWidth: 0 },
		closed: false,
	},
};

function titleForStation(station: RadioStation): string {
	return `${station.artist} - ${station.channelLabel}`;
}

export class WebampRadioAdapter {
	#audibleClock = new AudibleTrackClock();
	#cleanup: Array<() => void> = [];
	#compact = false;
	#host: HTMLElement | null = null;
	#layoutFrame: number | null = null;
	#listeners = new Set<Listener>();
	#observer: MutationObserver | null = null;
	#pendingStationRoute: string | null = null;
	#preferenceListener: PreferenceListener;
	#radioPreferences: RadioPreferences;
	#playbackStartTimer: number | null = null;
	#playbackTimer: number | null = null;
	#presentationFrame: number | null = null;
	#snapshot: RadioSnapshot;
	#stations: readonly RadioStation[];
	#webamp: WebampInstance | null = null;

	constructor(
		stations: readonly RadioStation[],
		initialStation: RadioStation,
		radioPreferences: RadioPreferences,
		preferenceListener: PreferenceListener,
	) {
		this.#stations = stations;
		this.#radioPreferences = restoreRadioPreferences(radioPreferences);
		this.#preferenceListener = preferenceListener;
		this.#snapshot = {
			closed: false,
			error: null,
			metadataFresh: false,
			playback: "loading",
			ready: false,
			station: initialStation,
			title: titleForStation(initialStation),
		};
	}

	getSnapshot(): RadioSnapshot {
		return { ...this.#snapshot };
	}

	subscribe(listener: Listener): () => void {
		this.#listeners.add(listener);
		listener(this.getSnapshot());
		return () => this.#listeners.delete(listener);
	}

	async initialize(host: HTMLElement, compact: boolean): Promise<void> {
		const { default: Webamp } = await import("webamp");
		if (!Webamp.browserIsSupported()) {
			this.#update({
				error: "This browser cannot run the classic Webamp player.",
				playback: "error",
			});
			return;
		}

		this.#host = host;
		this.#compact = compact;
		const webamp = new Webamp({
			initialTracks: stationTracks(this.#stations),
			windowLayout: compact ? COMPACT_LAYOUT : DESKTOP_LAYOUT,
			enableDoubleSizeMode: false,
			enableHotkeys: false,
			enableMediaSession: true,
			zIndex: 1,
			handleTrackDropEvent: () => [],
			handleAddUrlEvent: () => [],
			handleLoadListEvent: () => [],
			handleSaveListEvent: async () => null,
			// Webamp measures a live URL from the moment it connected. Replace that
			// session clock with the timestamped song clock supplied by AzuraCast.
			__customMiddlewares: [
				createAudibleClockMiddleware(
					this.#audibleClock,
					() => this.#webamp?.store.getState().playlist.currentTrack,
				),
			],
		});
		this.#webamp = webamp;
		this.#applyRadioPreferences();
		this.#cleanup.push(
			webamp.store.subscribe(() => this.#syncRadioPreferences()),
		);
		this.#configureAudioElement();

		const initialIndex = Math.max(
			0,
			this.#stations.indexOf(this.#snapshot.station),
		);
		webamp.setCurrentTrack(initialIndex);
		this.#cleanup.push(
			webamp.onTrackDidChange((track) => this.#handleTrackChange(track)),
			webamp.onClose(() => this.#update({ closed: true, playback: "closed" })),
		);

		try {
			await webamp.renderInto(host);
			this.#update({ ready: true });
			this.#bindLiveControls();
			this.#bindMediaErrors();
			this.#applyRadioPresentation();
			this.reflow(compact);
			this.#observer = new MutationObserver(() =>
				this.#scheduleRadioPresentation(),
			);
			this.#observer.observe(host, { childList: true, subtree: true });
			this.#playbackTimer = window.setInterval(() => {
				this.#syncPlaybackState();
				this.#syncLiveClock();
			}, 250);
		} catch (error) {
			console.error(
				JSON.stringify({
					event: "desktop_webamp_initialize_failed",
					message: String(error),
				}),
			);
			this.#update({
				error: "The radio player could not be initialized.",
				playback: "error",
			});
		}
	}

	async attemptAutoplay(): Promise<boolean> {
		if (!this.#webamp || !this.#snapshot.ready) return false;
		this.#prepareAudioOutput();
		this.playLive();
		await new Promise((resolve) => window.setTimeout(resolve, 900));
		const playing = this.#audioIsActuallyPlaying();
		if (playing) {
			this.#clearPlaybackStartTimer();
			this.#update({ playback: "playing" });
		} else if (!this.#audioPlaybackAttemptIsActive()) {
			this.#webamp.stop();
			this.#clearPlaybackStartTimer();
			this.#update({ playback: "blocked" });
		}
		return playing;
	}

	reflow(compact: boolean): void {
		const webamp = this.#webamp;
		if (!webamp) return;
		if (compact !== this.#compact) {
			this.#compact = compact;
			webamp.store.dispatch({
				type: "WINDOW_SIZE_CHANGED",
				windowId: "playlist",
				size: [0, compact ? 1 : 3],
			});
		}
		if (this.#layoutFrame !== null)
			window.cancelAnimationFrame(this.#layoutFrame);
		this.#layoutFrame = window.requestAnimationFrame(() => {
			this.#layoutFrame = null;
			this.#recenterWindowGroup();
		});
	}

	playLive(): void {
		const webamp = this.#webamp;
		if (!webamp) return;
		if (this.#snapshot.closed) webamp.reopen();
		this.#prepareAudioOutput();
		const state = webamp.store.getState();
		const currentTrack =
			state.playlist.currentTrack ?? state.playlist.trackOrder[0];
		if (currentTrack == null) return;
		this.#pendingStationRoute = this.#snapshot.station.route;
		this.#update({ closed: false, error: null, playback: "loading" });
		// Resuming the selected live station is not a station change. Use Webamp's
		// public transport so Play does not reload the stream or appear inert.
		webamp.play();
		this.#applyPlaybackMessage();
		this.#schedulePlaybackStartFailure();
	}

	pause(): void {
		const webamp = this.#webamp;
		if (!webamp) return;
		// Webamp's public pause() is a play/pause toggle. Live streams can be
		// audibly playing before its Redux status catches up, which makes that
		// toggle dispatch PLAY instead. Dispatch the underlying pause action so
		// the media element and the shell receive the same truthful state.
		webamp.store.dispatch({ type: "PAUSE" });
		this.#pendingStationRoute = null;
		this.#clearPlaybackStartTimer();
		this.#update({ playback: "paused" });
	}

	stop(): void {
		this.#clearPlaybackStartTimer();
		this.#pendingStationRoute = null;
		this.#webamp?.stop();
		this.#update({ playback: "stopped" });
	}

	reopen(): void {
		this.#webamp?.reopen();
		this.#pendingStationRoute = null;
		this.#update({ closed: false, playback: "stopped" });
	}

	selectStation(route: string, play = true): void {
		const webamp = this.#webamp;
		const index = this.#stations.findIndex(
			(station) => station.route === route,
		);
		if (!webamp || index < 0) return;
		const station = this.#stations[index];
		if (!station) return;
		if (play && station.route === this.#snapshot.station.route) {
			if (this.#snapshot.playback !== "loading") this.playLive();
			return;
		}
		const action = stationLoadAction(
			webamp.store.getState().playlist.trackOrder,
			index,
			play,
		);
		if (!action) return;
		if (play) {
			this.#pendingStationRoute = station.route;
			this.#prepareAudioOutput();
		}
		// Dispatch while the old station is still the active snapshot. Webamp
		// can synchronously report the outgoing track during this action; treating
		// that callback as a new selection would otherwise undo the user's choice.
		webamp.store.dispatch(action);
		this.#audibleClock.clear();
		document.title = `${station.name} — Zagreus Radio`;
		this.#update({
			station,
			title: titleForStation(station),
			metadataFresh: false,
			playback: play ? "loading" : this.#snapshot.playback,
			closed: false,
			error: null,
		});
		if (play) this.#schedulePlaybackStartFailure();
	}

	previousStation(): void {
		this.#navigateStation(-1);
	}

	nextStation(): void {
		this.#navigateStation(1);
	}

	applyLivePresentation(presentation: LivePresentation): void {
		const webamp = this.#webamp;
		if (!webamp || presentation.station.route !== this.#snapshot.station.route)
			return;
		const trackId = webamp.store.getState().playlist.currentTrack;
		if (trackId == null) return;
		const artist = String(
			presentation.track.artist || presentation.station.artist,
		).trim();
		const title = String(
			presentation.track.title || presentation.station.channelLabel,
		).trim();
		const displayTitle = [artist, title].filter(Boolean).join(" - ");
		this.#audibleClock.setTrack(
			presentation.track,
			presentation.elapsed,
			presentation.receivedAt,
		);
		webamp.store.dispatch({
			type: "SET_MEDIA_TAGS",
			id: trackId,
			artist,
			title,
			album: presentation.track.album || "",
			bitrate: 128_000,
			sampleRate: 44_100,
			numberOfChannels: 2,
		});
		webamp.store.dispatch({
			type: "SET_MEDIA_DURATION",
			id: trackId,
			duration: presentation.track.duration,
		});
		webamp.store.dispatch({
			type: "UPDATE_TIME_ELAPSED",
			elapsed: this.#audibleClock.elapsed(),
		});
		document.title = `${displayTitle} — Zagreus Radio`;
		this.#update({ metadataFresh: presentation.fresh, title: displayTitle });
	}

	destroy(): void {
		for (const cleanup of this.#cleanup.splice(0)) cleanup();
		this.#observer?.disconnect();
		this.#observer = null;
		if (this.#playbackTimer !== null) window.clearInterval(this.#playbackTimer);
		this.#playbackTimer = null;
		this.#clearPlaybackStartTimer();
		if (this.#layoutFrame !== null)
			window.cancelAnimationFrame(this.#layoutFrame);
		this.#layoutFrame = null;
		if (this.#presentationFrame !== null)
			window.cancelAnimationFrame(this.#presentationFrame);
		this.#presentationFrame = null;
		this.#webamp?.dispose();
		this.#pendingStationRoute = null;
		this.#audibleClock.clear();
		this.#webamp = null;
		this.#host = null;
		this.#listeners.clear();
	}

	#handleTrackChange(track: LoadedURLTrack | null): void {
		const station = this.#stationForUrl(track?.url);
		if (!station) return;
		// Webamp also emits this callback when the current live track is
		// replayed or its media tags change. Those are not station changes, and
		// clearing the clock here would make the live position fall back to the
		// stream-session timer until the next metadata poll.
		if (station.route === this.#snapshot.station.route) {
			queueMicrotask(() => this.#scheduleRadioPresentation());
			return;
		}
		this.#audibleClock.clear();
		document.title = `${station.name} — Zagreus Radio`;
		this.#update({
			station,
			title: titleForStation(station),
			metadataFresh: false,
		});
		queueMicrotask(() => this.#scheduleRadioPresentation());
	}

	#stationForUrl(url: string | undefined): RadioStation | null {
		if (!url) return null;
		const resolved = new URL(url, window.location.href).href;
		return (
			this.#stations.find(
				(station) =>
					new URL(station.stream, window.location.href).href === resolved,
			) ?? null
		);
	}

	#navigateStation(offset: number): void {
		const currentIndex = this.#stations.indexOf(this.#snapshot.station);
		const nextIndex =
			(currentIndex + offset + this.#stations.length) % this.#stations.length;
		const station = this.#stations[nextIndex];
		if (station) this.selectStation(station.route, true);
	}

	#applyRadioPreferences(): void {
		const webamp = this.#webamp;
		if (!webamp) return;
		webamp.setVolume(this.#radioPreferences.volume);
		for (const slider of RADIO_EQUALIZER_SLIDERS) {
			webamp.store.dispatch({
				type: "SET_BAND_VALUE",
				band: slider,
				value: this.#radioPreferences.equalizer.sliders[slider],
			});
		}
		webamp.store.dispatch({
			type: "SET_EQ_AUTO",
			value: this.#radioPreferences.equalizer.auto,
		});
		webamp.store.dispatch({
			type: this.#radioPreferences.equalizer.enabled
				? "SET_EQ_ON"
				: "SET_EQ_OFF",
		});
	}

	#syncRadioPreferences(): void {
		const webamp = this.#webamp;
		if (!webamp) return;
		const next = radioPreferencesFromWebampState(webamp.store.getState());
		if (radioPreferencesEqual(next, this.#radioPreferences)) return;
		this.#radioPreferences = next;
		this.#preferenceListener(next);
	}

	#prepareAudioOutput(): void {
		this.#configureAudioElement();
		const media = this.#webamp?.media as PrivateMedia | undefined;
		if (media?._context?.state === "suspended") void media._context.resume();
	}

	#configureAudioElement(): void {
		const media = this.#webamp?.media as PrivateMedia | undefined;
		const audio = media?._source?._audio;
		if (!audio) return;
		audio.autoplay = true;
		audio.preload = "auto";
		audio.setAttribute("playsinline", "");
	}

	#audioIsActuallyPlaying(): boolean {
		const media = this.#webamp?.media as PrivateMedia | undefined;
		const audio = media?._source?._audio;
		if (!audio) return false;
		const sourceRoute =
			this.#stationForUrl(audio.currentSrc)?.route ??
			this.#stationForUrl(audio.src)?.route ??
			null;
		return mediaIsReadyForStation({
			contextRunning: media?._context?.state === "running",
			ended: audio.ended,
			paused: audio.paused,
			pendingRoute: this.#pendingStationRoute,
			readyState: audio.readyState,
			sourceRoute,
		});
	}

	#audioPlaybackAttemptIsActive(): boolean {
		const media = this.#webamp?.media as PrivateMedia | undefined;
		const audio = media?._source?._audio;
		if (!audio) return false;
		return mediaPlaybackAttemptIsActive({
			contextRunning: media?._context?.state === "running",
			ended: audio.ended,
			paused: audio.paused,
		});
	}

	#syncPlaybackState(): void {
		const webamp = this.#webamp;
		if (!webamp || this.#snapshot.playback === "error" || this.#snapshot.closed)
			return;
		const playback = playbackStateFromObservation(
			this.#snapshot.playback,
			this.#audioIsActuallyPlaying(),
			webamp.getMediaStatus(),
		);
		if (playback === "playing") {
			this.#pendingStationRoute = null;
			this.#clearPlaybackStartTimer();
		}
		this.#update({ playback });
		if (playback === "loading") this.#applyPlaybackMessage();
	}

	#syncLiveClock(): void {
		const webamp = this.#webamp;
		if (!webamp || !this.#audibleClock.getAnchor()) return;
		const elapsed = this.#audibleClock.elapsed();
		if (webamp.store.getState().media.timeElapsed === elapsed) return;
		webamp.store.dispatch({ type: "UPDATE_TIME_ELAPSED", elapsed });
	}

	#bindLiveControls(): void {
		const host = this.#host;
		if (!host) return;
		this.#captureClick(host, "#play", () => this.playLive());
		this.#captureClick(host, "#pause", () => {
			if (this.#snapshot.playback === "paused") this.playLive();
			else this.pause();
		});
		this.#captureClick(host, "#stop", () => this.stop());
		this.#captureClick(host, "#previous", () => this.previousStation());
		this.#captureClick(host, "#next", () => this.nextStation());
	}

	#bindMediaErrors(): void {
		const media = this.#webamp?.media as PrivateMedia | undefined;
		const audio = media?._source?._audio;
		if (!audio) return;
		const handleError = () => this.#markStreamUnavailable();
		const handlePlaybackProgress = () => this.#syncPlaybackState();
		audio.addEventListener("error", handleError);
		audio.addEventListener("canplay", handlePlaybackProgress);
		audio.addEventListener("playing", handlePlaybackProgress);
		this.#cleanup.push(() => {
			audio.removeEventListener("error", handleError);
			audio.removeEventListener("canplay", handlePlaybackProgress);
			audio.removeEventListener("playing", handlePlaybackProgress);
		});
	}

	#schedulePlaybackStartFailure(): void {
		this.#clearPlaybackStartTimer();
		this.#playbackStartTimer = window.setTimeout(() => {
			this.#playbackStartTimer = null;
			if (
				this.#snapshot.playback === "loading" &&
				!this.#audioIsActuallyPlaying()
			) {
				this.#markStreamUnavailable();
			}
		}, PLAYBACK_START_TIMEOUT_MS);
	}

	#clearPlaybackStartTimer(): void {
		if (this.#playbackStartTimer !== null)
			window.clearTimeout(this.#playbackStartTimer);
		this.#playbackStartTimer = null;
	}

	#markStreamUnavailable(): void {
		this.#clearPlaybackStartTimer();
		this.#pendingStationRoute = null;
		this.#webamp?.stop();
		this.#update({
			error:
				"The live stream did not respond. Check your connection and try again.",
			playback: "error",
		});
	}

	#applyPlaybackMessage(): void {
		this.#webamp?.store.dispatch({
			type: "SET_USER_MESSAGE",
			message: playbackMessage(this.#snapshot),
		});
	}

	#captureClick(root: Element, selector: string, action: () => void): void {
		const listener = (event: Event) => {
			const target = event.target;
			if (
				!(target instanceof Element) ||
				!root.contains(target) ||
				!target.closest(selector)
			)
				return;
			event.preventDefault();
			event.stopImmediatePropagation();
			action();
		};
		root.addEventListener("click", listener, true);
		this.#cleanup.push(() => root.removeEventListener("click", listener, true));
	}

	#applyRadioPresentation(): void {
		const root = this.#host?.querySelector("#webamp");
		if (!root) return;
		root.classList.add("zagreus-webamp-radio");
		const position = root.querySelector("#position");
		if (position instanceof HTMLInputElement) {
			position.disabled = true;
			position.setAttribute("aria-label", "Live broadcast song progress");
		}
		const rows = root.querySelectorAll(
			"#playlist-window .playlist-track-titles > div",
		);
		const scrollPosition =
			Number(this.#webamp?.store.getState().display.playlistScrollPosition) ||
			0;
		const overflow = Math.max(0, this.#stations.length - rows.length);
		const offset = overflow
			? Math.round(
					(Math.min(100, Math.max(0, scrollPosition)) / 100) * overflow,
				)
			: 0;
		for (const [index, row] of [...rows].entries()) {
			const stationIndex = offset + index;
			const station = this.#stations[stationIndex];
			const label = station
				? `${stationIndex + 1}. ${station.channelLabel}`
				: "";
			if (label && row.textContent !== label) row.textContent = label;
		}
		for (const duration of root.querySelectorAll(
			"#playlist-window .playlist-track-durations > div",
		)) {
			if (duration.textContent !== "LIVE") duration.textContent = "LIVE";
		}
	}

	#scheduleRadioPresentation(): void {
		if (this.#presentationFrame !== null) return;
		this.#presentationFrame = window.requestAnimationFrame(() => {
			this.#presentationFrame = null;
			this.#applyRadioPresentation();
			this.#applyPlaybackMessage();
		});
	}

	#recenterWindowGroup(): void {
		const webamp = this.#webamp;
		const host = this.#host;
		if (!webamp || !host || host.hidden) return;
		const hostRect = host.getBoundingClientRect();
		const windows = ["main", "equalizer", "playlist"]
			.map((windowId) => ({
				windowId,
				element: host.querySelector<HTMLElement>(`#${windowId}-window`),
			}))
			.filter(
				(entry): entry is { windowId: string; element: HTMLElement } =>
					entry.element !== null && entry.element.offsetParent !== null,
			);
		if (!windows.length) return;

		const rects = windows.map(({ element }) => element.getBoundingClientRect());
		const left = Math.min(...rects.map((rect) => rect.left));
		const top = Math.min(...rects.map((rect) => rect.top));
		const right = Math.max(...rects.map((rect) => rect.right));
		const bottom = Math.max(...rects.map((rect) => rect.bottom));
		const groupWidth = right - left;
		const groupHeight = bottom - top;
		const availableHeight = Math.max(1, host.clientHeight - 84);
		const targetLeft = Math.max(
			8,
			Math.round((host.clientWidth - groupWidth) / 2),
		);
		const targetTop = Math.max(
			18,
			Math.round((availableHeight - groupHeight) / 2),
		);
		const deltaX = targetLeft - (left - hostRect.left);
		const deltaY = targetTop - (top - hostRect.top);
		if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

		const stateWindows = webamp.store.getState().windows.genWindows;
		const positions: Record<string, { x: number; y: number }> = {};
		for (const { windowId } of windows) {
			const position = stateWindows[windowId]?.position;
			if (!position) continue;
			positions[windowId] = {
				x: Math.round(position.x + deltaX),
				y: Math.round(position.y + deltaY),
			};
		}
		webamp.store.dispatch({
			type: "UPDATE_WINDOW_POSITIONS",
			positions,
			absolute: true,
		});
	}

	#update(update: Partial<RadioSnapshot>): void {
		const next = { ...this.#snapshot, ...update };
		if (
			next.closed === this.#snapshot.closed &&
			next.error === this.#snapshot.error &&
			next.metadataFresh === this.#snapshot.metadataFresh &&
			next.playback === this.#snapshot.playback &&
			next.ready === this.#snapshot.ready &&
			next.station === this.#snapshot.station &&
			next.title === this.#snapshot.title
		) {
			return;
		}
		this.#snapshot = next;
		this.#applyPlaybackMessage();
		for (const listener of this.#listeners) listener(this.getSnapshot());
	}
}
